import { ConditionalAccessPolicy } from '@microsoft/microsoft-graph-types-beta';
import { select, Store } from '@ngrx/store';
import { combineLatest, map, Observable, skipUntil } from 'rxjs';
import * as CAPMagic from './cap-generic';
import * as CAPAppEnforcedRestrictionUnmanagedDevices from './CAPAppEnforcedRestrictionUnmanagedDevices';
import * as CAPAppProtectionPolicy from './CAPAppProtectionPolicy';
import * as CAPEnforceMFA from './CAPEnforceMFA';
import * as CAPLegacyAuth from './CAPLegacyAuth';
import * as NoPersistentBrowserSessionCAP from './CAPNoPersistentBrowsers';
import * as CAPRequireCompliantOrHybridAzureADJoinedDevice from './CAPRequireCompliantOrHybridAzureADJoinedDevice';
import * as CAPRestrictAzureAccess from './CAPRestrictAzureAccess';
import * as CAPRestrictMicrosoftAdminPortalAccess from './CAPRestrictMicrosoftAdminPortalAccess';
import * as CAPRiskySignin from './CAPRiskySignin';
import * as CAPSecuringSecurityInfoRegistration from './CAPSecuringSecurityInfoRegistration';
import * as CAPUnSupportedDeviceBlock from './CAPUnSupportedDeviceBlock';
import { CapGrantControlComponent } from './shared/controls/cap-grant-control/cap-grant-control.component';
import { CapSessionControlComponent } from './shared/controls/cap-session-control/cap-session-control.component';
import { CapConditionsComponent } from './shared/controls/cap-conditions/cap-conditions.component';
import { CapTargetResourcesControlComponent } from './shared/controls/cap-target-resources-control/cap-target-resources-control.component';
import { CapMappingDialogComponent } from './shared/cap-mapping-dialog/cap-mapping-dialog.component';
import { CapGenericBaselineComponent } from './shared/cap-generic-baseline/cap-generic-baseline.component';
import { CreateCountryNamedLocationComponent } from './shared/controls/cap-network-control/named-locations-dialog/create-country-named-location/create-country-named-location.component';
import { CreateIpNamedLocationComponent } from './shared/controls/cap-network-control/named-locations-dialog/create-ip-named-location/create-ip-named-location.component';
import { NamedLocationsDialogComponent } from './shared/controls/cap-network-control/named-locations-dialog/named-locations-dialog.component';
import { CapNetworkControlComponent } from './shared/controls/cap-network-control/cap-network-control.component';
import { Status } from 'projects/angular-clarity/src/app/stores/status.interface';
import { selectSwaySpecStatus } from 'projects/angular-clarity/src/app/stores/root.store';
import { client } from 'projects/angular-clarity/src/app/stores/client';

export const BaselineComponents = [
    CAPEnforceMFA.BaselineComponent,
    CAPLegacyAuth.BaselineComponent,
    CAPSecuringSecurityInfoRegistration.BaselineComponent,
    CAPAppProtectionPolicy.BaselineComponent,
    CAPAppEnforcedRestrictionUnmanagedDevices.BaselineComponent,
    CAPRequireCompliantOrHybridAzureADJoinedDevice.BaselineComponent,
    CAPRiskySignin.BaselineComponent,
    CAPRestrictAzureAccess.BaselineComponent,
    CAPUnSupportedDeviceBlock.BaselineComponent,
    NoPersistentBrowserSessionCAP.BaselineComponent,
    CAPRestrictMicrosoftAdminPortalAccess.BaselineComponent,
    CAPMagic.BaselineComponent,
];

export const SharedCAPComponents = [
    CapGrantControlComponent,
    CapSessionControlComponent,
    CapConditionsComponent,
    CapNetworkControlComponent,
    CreateCountryNamedLocationComponent,
    CreateIpNamedLocationComponent,
    CapTargetResourcesControlComponent,
    NamedLocationsDialogComponent,
    CapMappingDialogComponent,
    CapGenericBaselineComponent,
];

export function selectCapData(store: Store, tenant: string, tag: string): Observable<ConditionalAccessPolicy[]> {
    const baseline$ = store.pipe(select(client(tenant).sway.baselines.bySpecTag(tag)));
    const policies$ = store.pipe(select(client(tenant).graph.conditionalAccessPolicy.all));

    const baselineStatus$ = store.pipe(select(client(tenant).sway.baselines.status));
    const swayStatus$ = store.pipe(select(selectSwaySpecStatus));
    const policiesStatus$ = store.pipe(select(client(tenant).graph.conditionalAccessPolicy.status));
    const authenticationStrengthStatus$ = store.pipe(select(client(tenant).graph.authenticationStrengthPolicy.status));
    const namedLocationStatus$ = store.pipe(select(client(tenant).graph.authenticationStrengthPolicy.status));

    // const policies$ = select(client(tenant).graph.compliancePolicies.windows10.all);

    const ready$ = combineLatest([
        baselineStatus$,
        swayStatus$,
        policiesStatus$,
        authenticationStrengthStatus$,
        namedLocationStatus$,
    ]).pipe(map((statuses) => statuses.every((status) => status.loaded)));

    return combineLatest([policies$, baseline$]).pipe(
        skipUntil(ready$),
        map(([policies, baseline]) => {
            if (!baseline) return [];
            const key = baseline.key;

            if (key) {
                const policy = policies.find((res) => res?.id === key);

                return policy ? [policy] : [{}];
            } else {
                const displayName = baseline?.schema?.contains?.properties?.policy?.properties?.displayName.const;
                const policy = policies.find((res) => res?.displayName === displayName);
                return policy ? [policy] : [{}];
            }
        }),
    );
}

export function selectCapStatus(store: Store, tenant: string): Observable<Status> {
    const swayStatus$ = store.pipe(select(selectSwaySpecStatus));
    const policiesStatus$ = store.pipe(select(client(tenant).graph.conditionalAccessPolicy.status));
    const authStrengthStatus$ = store.pipe(select(client(tenant).graph.authenticationStrengthPolicy.status));
    const namedLocationStatus$ = store.pipe(select(client(tenant).graph.authenticationStrengthPolicy.status));

    // const policies$ = select(client(tenant).graph.compliancePolicies.windows10.all);

    const status$ = combineLatest([swayStatus$, policiesStatus$, authStrengthStatus$, namedLocationStatus$]).pipe(
        map(([s, p, a, n]) => ({
            loaded: s.loaded && p.loaded && a.loaded && n.loaded,
            loading: s.loading || p.loading || a.loading || n.loading,
            error: s.error || p.error || a.error || n.error,
            updating: s.updating || p.updating || a.updating || n.updating,
        })),
    );

    return status$;
}
